import { useMemo } from 'react';

import { EntityType } from '~/types/entity';

import { useGetUserUtils } from '~/data/user';

import {
  type ButtonConfig as ButtonConfigBase,
  ButtonSplit as ButtonSplitBase,
  type ButtonSplitProps,
} from '@vestigas/ui/molecules';

export type ButtonConfig = ButtonConfigBase;

type CustomerButtonSplitProps = ButtonSplitProps & {
  requiresPermissionFor?: EntityType;
};

/**
 * A proxy for the ui package's ButtonSplit component that adds permission checking.
 * And applies our "legacy" styling.
 */
export const ButtonSplit = ({
  buttonProps,
  className = 'primary-button',
  config,
  dataTestId,
  onClickShowOptions,
  menuItemSx,
  menuPaperSx,
  requiresPermissionFor,
  startIcon,
  variant = 'contained',
}: CustomerButtonSplitProps) => {
  const { UserUtils } = useGetUserUtils();
  const permissionsMap = useMemo(
    () =>
      new Map([
        ['product', () => true],
        ['site', UserUtils.isSiteCreateAllowedUser],
        ['user', UserUtils.isUserCreateAllowedUser],
      ]),
    [UserUtils],
  );

  const isMissingPermissions =
    requiresPermissionFor !== undefined
      ? !permissionsMap.get(requiresPermissionFor)?.()
      : false;

  if (isMissingPermissions) {
    return null;
  }

  const internalButtonProps = {
    ...buttonProps,
    className,
  };
  return (
    <ButtonSplitBase
      buttonProps={internalButtonProps}
      className={className}
      config={config}
      dataTestId={dataTestId}
      onClickShowOptions={onClickShowOptions}
      menuItemSx={menuItemSx}
      menuPaperSx={menuPaperSx}
      startIcon={startIcon}
      variant={variant}
    />
  );
};
